import { useMemo, useState } from 'react';
import { tierName } from '@h2h/shared';
import { TierDot } from '../DivisionStrip';
import { Placeholder } from '../../ui/Placeholder';
import { SegmentedControl } from '../../ui/SegmentedControl';
import { Trophy } from '../../ui/icons';
import { Avatar } from '../../ui/bits';
import { cap, formatCount, ordinalSuffix } from '../../../lib/format';
import { type LeagueSeasonRow } from '../../../hooks/useQueries';
import { useOpenProfile } from '../../../hooks/useOpenProfile';
import { cn } from '../../../lib/cn';

type LeagueMetric = 'total' | 'avg';

const LEAGUE_METRICS: { key: LeagueMetric; label: string }[] = [
  { key: 'total', label: 'All-time' },
  { key: 'avg', label: 'Per round' }
];

/**
 * The whole league ranked by vanity season points. Unlike the Live view (the
 * division ladder that resets every round and is the "real" game) this is a flat,
 * everyone-in-one-table leaderboard — display only, so it never gates tier or
 * matchmaking. Two lenses: all-time TOTAL (rewards a full, consistent season) and
 * per-round AVERAGE (join-date neutral, so a late starter isn't buried). The
 * helper line keeps that promise explicit to the player.
 */
export function LeagueSeasonStandingsView({
  rows,
  leagueName
}: {
  rows: LeagueSeasonRow[];
  leagueName: string;
}) {
  const [metric, setMetric] = useState<LeagueMetric>('total');

  const ranked = useMemo(() => {
    const value = (r: LeagueSeasonRow) => (metric === 'avg' ? r.avgPerRound : r.seasonPoints);
    return [...rows].sort(
      (a, b) =>
        value(b) - value(a) ||
        b.seasonPoints - a.seasonPoints ||
        b.tier - a.tier ||
        a.displayName.localeCompare(b.displayName)
    );
  }, [rows, metric]);

  const myIndex = ranked.findIndex((r) => r.isYou);
  const myRank = myIndex >= 0 ? myIndex + 1 : null;
  const me = myIndex >= 0 ? ranked[myIndex] : null;
  const anyScores = rows.some((r) => r.roundsPlayed > 0);

  if (rows.length === 0 || !anyScores) {
    return (
      <Placeholder
        Icon={Trophy}
        title="No season points yet"
        body={`Once rounds settle in ${leagueName}, every player's season points stack up here.`}
      />
    );
  }

  return (
    <>
      <div className="flex items-center gap-[15px] rounded-[22px] border border-black/5 bg-card p-[18px] shadow-tile">
        <div className="text-center">
          <div className="num text-[40px] font-extrabold leading-none tracking-title text-ink">
            {myRank ?? '—'}
            <span className="text-[18px] font-bold text-ink3">{myRank ? ordinalSuffix(myRank) : ''}</span>
          </div>
          <div className="mt-1 text-[11px] font-semibold text-ink2">of {ranked.length}</div>
        </div>
        <div className="w-px self-stretch bg-black/[0.07]" />
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-extrabold tracking-snug text-ink">Season leaderboard</div>
          <div className="mt-[2px] text-[12px] font-medium text-ink2">
            {me
              ? metric === 'avg'
                ? `You average ${me.avgPerRound.toFixed(1)} pts a round`
                : `You've scored ${formatCount(me.seasonPoints)} pts this season`
              : `Every player in ${leagueName}`}
          </div>
        </div>
      </div>

      <SegmentedControl
        value={metric}
        onChange={setMetric}
        options={LEAGUE_METRICS}
        layoutId="league-metric-pill"
        trackClassName="rounded-[12px] bg-[#E6E6EC] p-1"
        itemClassName="rounded-[9px] py-[7px] text-[12px]"
      />

      <p className="-mt-1 px-1 text-[11px] font-medium leading-snug text-ink3">
        {metric === 'avg'
          ? 'Average points per settled round — fair to anyone who joined late.'
          : 'Total points scored across every round this season.'}{' '}
        Vanity only — your tier is still won round by round.
      </p>

      <div className="flex flex-col gap-1 pb-2">
        {ranked.map((row, i) => (
          <LeagueSeasonRowItem key={row.userId} row={row} rank={i + 1} metric={metric} />
        ))}
      </div>
    </>
  );
}

function LeagueSeasonRowItem({
  row,
  rank,
  metric
}: {
  row: LeagueSeasonRow;
  rank: number;
  metric: LeagueMetric;
}) {
  const openProfile = useOpenProfile();
  const you = row.isYou;
  const primary = metric === 'avg' ? row.avgPerRound.toFixed(1) : formatCount(row.seasonPoints);
  const rounds = `${row.roundsPlayed} ${row.roundsPlayed === 1 ? 'rd' : 'rds'}`;
  const secondary = metric === 'avg' ? `${formatCount(row.seasonPoints)} pts · ${rounds}` : rounds;

  return (
    <button
      type="button"
      onClick={() => openProfile(row.userId, you)}
      className={cn(
        'tap flex w-full items-center gap-3 rounded-[14px] px-[11px] py-[11px] text-left transition-colors',
        you ? 'bg-night shadow-[0_12px_26px_-12px_rgba(0,0,0,0.5)]' : 'active:bg-fill2/70'
      )}
    >
      <span className={cn('num w-6 shrink-0 text-center text-[14px] font-extrabold', you ? 'text-accent' : 'text-ink')}>
        {rank}
      </span>
      <Avatar name={row.displayName} tone={row.avatarTone} src={row.photoURL} size={34} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className={cn('truncate text-[14px] font-bold tracking-snug', you ? 'text-white' : 'text-ink')}>
            {you ? 'You' : row.displayName}
          </span>
          {rank === 1 && <Trophy width={13} height={13} strokeWidth={1.6} className="shrink-0 text-gold-bottom" />}
        </div>
        <div className="mt-[2px] flex items-center gap-[5px]">
          <TierDot tierIndex={row.tier} />
          <span className={cn('text-[10px] font-semibold', you ? 'text-[#9A9AA4]' : 'text-ink2')}>
            {cap(tierName(row.tier))}
          </span>
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className={cn('num text-[16px] font-extrabold leading-none', you ? 'text-white' : 'text-ink')}>
          {primary}
          <span className={cn('ml-0.5 text-[10px] font-bold', you ? 'text-[#9A9AA4]' : 'text-ink3')}>
            {metric === 'avg' ? '/rd' : 'pts'}
          </span>
        </div>
        <div className={cn('mt-[3px] text-[10px] font-semibold', you ? 'text-[#9A9AA4]' : 'text-ink3')}>
          {secondary}
        </div>
      </div>
    </button>
  );
}
